import {
   EmissionFactorRecord,
   EmissionFactorsApiResponse,
   FeedstockYearlyValue,
   toEmissionFactorRecord,
   toUpdateRequest,
} from '../types/emission-factor.api';
import { useCallback, useEffect, useState } from 'react';

import APIService from '../../../services/api.service';
import { FeedstockKey, MetricKey } from '../types/emission-factor.constants';
import { ToastService } from '../../../services/toast.service';

const extractRecordFromResponse = (response: EmissionFactorsApiResponse, gradeId: string): EmissionFactorRecord | null => {
   for (const ef of response.emissionFactors) {
      for (const mt of ef.materialTypes) {
         for (const st of mt.subTypes) {
            const mg = (st.materialGrades ?? []).find((g) => g.id === gradeId);
            if (mg) {
               return toEmissionFactorRecord({
                  id: mg.id,
                  name: mg.name ?? mg.id,
                  categoryId: ef.id,
                  materialTypeId: mt.id,
                  subtypeId: st.id,
                  feedstocks: mg.feedstocks ?? [],
                  groupId: mg.groupId,
                  groupName: mg.groupName,
               });
            }
         }
      }
   }
   return null;
};

export interface EmissionFactorRecordState {
   currentRecord: EmissionFactorRecord | null;
   originalRecord: EmissionFactorRecord | null;
   loadingRecord: boolean;
   isSaving: boolean;
   saveError: string | null;
   updateYearlyValue: (year: number, feedstock: FeedstockKey, field: MetricKey, value: number | null) => void;
   saveChanges: (comments?: Record<string, string>) => Promise<void>;
   resetChanges: () => void;
}

interface CacheRef {
   byGrade: Record<string, EmissionFactorsApiResponse>;
}

export function useEmissionFactorRecord(
   selectedGrade: string | null,
   cachedResponsesRef: React.MutableRefObject<CacheRef>,
   onChangesFlushed: () => void
): EmissionFactorRecordState {
   const [currentRecord, setCurrentRecord] = useState<EmissionFactorRecord | null>(null);
   const [originalRecord, setOriginalRecord] = useState<EmissionFactorRecord | null>(null);
   const [loadingRecord, setLoadingRecord] = useState(false);
   const [isSaving, setIsSaving] = useState(false);
   const [saveError, setSaveError] = useState<string | null>(null);

   useEffect(() => {
      if (!selectedGrade) {
         setCurrentRecord(null);
         setOriginalRecord(null);
         return;
      }

      let isCurrent = true;
      const load = async () => {
         setLoadingRecord(true);
         try {
            let response = cachedResponsesRef.current.byGrade[selectedGrade];
            if (!response) {
               response = await APIService.getEmissionFactors({ gradeId: selectedGrade });
               cachedResponsesRef.current.byGrade[selectedGrade] = response;
            }
            if (!isCurrent) return;
            const record = extractRecordFromResponse(response, selectedGrade);
            setCurrentRecord(record);
            setOriginalRecord(record ? structuredClone(record) : null);
         } catch (err) {
            if (isCurrent) {
               setSaveError('Failed to load emission factor data');
               console.error('Error loading record:', err);
            }
         } finally {
            if (isCurrent) setLoadingRecord(false);
         }
      };

      load();
      return () => {
         isCurrent = false;
      };
   }, [selectedGrade, cachedResponsesRef]);

   const updateYearlyValue = useCallback((year: number, feedstock: FeedstockKey, field: MetricKey, value: number | null) => {
      setCurrentRecord((prev) => {
         if (!prev) return prev;
         return {
            ...prev,
            yearlyValues: prev.yearlyValues.map((yv) => {
               if (yv.year !== year) return yv;
               return { ...yv, [feedstock]: { ...yv[feedstock], [field]: value } as FeedstockYearlyValue };
            }),
         };
      });
   }, []);

   const saveChanges = useCallback(
      async (comments?: Record<string, string>) => {
         if (!currentRecord || !originalRecord || !selectedGrade || isSaving) return;

         setIsSaving(true);
         setSaveError(null);

         try {
            const request = toUpdateRequest(originalRecord, currentRecord, comments);
            await APIService.updateEmissionFactors(request);

            delete cachedResponsesRef.current.byGrade[selectedGrade];
            const response = await APIService.getEmissionFactors({ gradeId: selectedGrade });
            cachedResponsesRef.current.byGrade[selectedGrade] = response;

            const refreshed = extractRecordFromResponse(response, selectedGrade);
            setCurrentRecord(refreshed);
            setOriginalRecord(refreshed ? structuredClone(refreshed) : null);
            onChangesFlushed();

            const count = request.length;
            ToastService.success(`${count} emission factor${count !== 1 ? 's' : ''} updated`);
         } catch (err) {
            setSaveError('Failed to save changes');
            console.error('Error saving:', err);
         } finally {
            setIsSaving(false);
         }
      },
      [currentRecord, originalRecord, selectedGrade, isSaving, cachedResponsesRef, onChangesFlushed]
   );

   const resetChanges = useCallback(() => {
      if (originalRecord) {
         setCurrentRecord(structuredClone(originalRecord));
         onChangesFlushed();
      }
   }, [originalRecord, onChangesFlushed]);

   return {
      currentRecord,
      originalRecord,
      loadingRecord,
      isSaving,
      saveError,
      updateYearlyValue,
      saveChanges,
      resetChanges,
   };
}
